import { Link } from "react-router-dom";
import {
  Handshake,
  CalendarClock,
  Rocket,
  TrendingUp,
  Users,
  ArrowRight,
  type LucideIcon,
} from "lucide-react";
import { PublicPageLayout, PageHero, SectionHead } from "./PublicPageLayout";

/**
 * PlaybooksPage (/playbooks) - competitive-intelligence use-case playbooks.
 *
 * Each playbook is a real, repeatable way teams use NovaSynth: the situation,
 * the steps, and the outcome. Honest and concrete - no invented metrics or
 * customers, just the workflow itself.
 */

interface Playbook {
  key: string;
  Icon: LucideIcon;
  tint: string;
  chip: string;
  hex: string;
  title: string;
  when: string;
  steps: string[];
  outcome: string;
}

const PLAYBOOKS: Playbook[] = [
  {
    key: "deal",
    Icon: Handshake,
    tint: "text-emerald-300",
    chip: "bg-emerald-500/10 border-emerald-500/30",
    hex: "#22c55e",
    title: "Prep for a competitive deal",
    when: "A prospect is evaluating you against a named competitor and the call is this week.",
    steps: [
      "Run an analysis on the competitor the buyer mentioned.",
      "Read the threat score for how they stack up against your position specifically.",
      "Open the battlecard for their strengths, their gaps, and the objection-handling lines.",
      "Export the brief and share it with the rep before the call.",
    ],
    outcome: "The rep walks in knowing exactly where to push and which questions to expect.",
  },
  {
    key: "watch",
    Icon: CalendarClock,
    tint: "text-cyan-300",
    chip: "bg-cyan-500/10 border-cyan-500/30",
    hex: "#22d3ee",
    title: "Weekly competitor watch",
    when: "You want a standing pulse on the handful of companies that move your market.",
    steps: [
      "Add the competitors that matter to your watchlist.",
      "Re-run each one on a regular cadence - a Monday rhythm works well.",
      "Compare the new brief against the run history to spot what changed.",
      "Forward anything that crossed into a higher threat band to the team.",
    ],
    outcome: "Nobody is surprised in the Monday meeting - the shifts are already on the radar.",
  },
  {
    key: "launch",
    Icon: Rocket,
    tint: "text-purple-300",
    chip: "bg-purple-500/10 border-purple-500/30",
    hex: "#a855f7",
    title: "React to a competitor launch",
    when: "A rival just announced something and leadership wants a read by end of day.",
    steps: [
      "Run an analysis the moment the news lands.",
      "Let the news, document, web, and video specialists pull the announcement, the framing, and the demo.",
      "Use the threat score and reasoning to size whether this changes your position.",
      "Export the brief as the basis for your internal response note.",
    ],
    outcome: "A grounded, cited read in hours instead of a scramble of half-confirmed rumours.",
  },
  {
    key: "board",
    Icon: TrendingUp,
    tint: "text-blue-300",
    chip: "bg-blue-500/10 border-blue-500/30",
    hex: "#3b82f6",
    title: "Brief the board on the landscape",
    when: "You owe leadership a clear, current view of the competitive field.",
    steps: [
      "Run fresh briefs across your tracked set so every read is current.",
      "Note the threat band for each and what is driving it this quarter.",
      "Pull the reasoning behind any score that moved since last time.",
      "Export the briefs into your board materials, citations intact.",
    ],
    outcome: "A defensible landscape slide where every claim links back to a source.",
  },
  {
    key: "enablement",
    Icon: Users,
    tint: "text-amber-300",
    chip: "bg-amber-500/10 border-amber-500/30",
    hex: "#f59e0b",
    title: "Keep sales enablement current",
    when: "Your battlecards are going stale and reps are improvising in deals.",
    steps: [
      "Re-run the competitors your reps face most often.",
      "Refresh each battlecard from the latest brief, not last quarter's notes.",
      "Export the updated cards and drop them into your enablement library.",
      "Repeat whenever a tracked competitor crosses into a higher threat band.",
    ],
    outcome: "Reps carry talk tracks that match what the competitor is actually doing now.",
  },
];

export function PlaybooksPage({ authed = false }: { authed?: boolean }) {
  return (
    <PublicPageLayout authed={authed}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-8">
        <PageHero
          eyebrow="Playbooks"
          title={
            <>
              Ways teams put{" "}
              <span className="ns-gradient-text">NovaSynth</span> to work
            </>
          }
          sub="Practical, repeatable plays for the moments competitive intelligence actually earns its keep - from a deal this week to a standing watch on your market."
        />

        <section className="mt-14">
          <SectionHead
            eyebrow="Use cases"
            title="Pick the play that fits the moment"
            sub="Each one is a short, concrete workflow. Start with the situation closest to yours and adapt from there."
          />

          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PLAYBOOKS.map((p, i) => (
              <article
                key={p.key}
                className="ns-reveal group relative overflow-hidden rounded-2xl border border-[#1a2640] bg-[#0d1424]/70 p-6 sm:p-7"
                style={{ ["--ns-delay" as string]: `${120 + i * 70}ms` }}
              >
                <div
                  className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-0 blur-2xl transition-opacity duration-300 group-hover:opacity-100"
                  style={{ background: p.hex }}
                  aria-hidden="true"
                />
                <div className="relative flex items-center gap-3">
                  <div
                    className={`flex h-11 w-11 items-center justify-center rounded-xl border ${p.chip}`}
                  >
                    <p.Icon className={`w-5 h-5 ${p.tint}`} />
                  </div>
                  <h3 className="text-[17px] font-semibold text-white">{p.title}</h3>
                </div>

                <p className="relative mt-4 text-sm leading-relaxed text-slate-300">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                    When
                  </span>
                  <span className="mt-1 block text-slate-400">{p.when}</span>
                </p>

                <ol className="relative mt-4 flex flex-col gap-2.5">
                  {p.steps.map((s, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border border-[#1a2640] bg-white/[0.04] font-mono text-[11px] font-semibold text-[#22d3ee]">
                        {idx + 1}
                      </span>
                      <span className="text-sm leading-relaxed text-slate-400">{s}</span>
                    </li>
                  ))}
                </ol>

                <div className="relative mt-5 rounded-xl border border-[#1a2640] bg-white/[0.02] px-4 py-3">
                  <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-400">
                    Outcome
                  </span>
                  <p className="mt-1 text-sm leading-relaxed text-slate-300">{p.outcome}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20">
          <div className="ns-reveal relative overflow-hidden rounded-3xl border border-brand-600/30 bg-gradient-to-br from-brand-700/25 via-[#0d1424] to-[#22d3ee]/10 p-10 sm:p-12 text-center">
            <div className="ns-aurora" aria-hidden="true" />
            <div className="relative">
              <h2 className="font-mono text-2xl sm:text-3xl font-bold tracking-tight text-white">
                New to the workflow?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-300">
                The product guide walks through how a brief is built, how to read the threat score, and
                how to export - the foundation under every play here.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/guides"
                  className="group inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-500 px-6 py-3 text-sm font-semibold text-white shadow-glow-brand-sm transition-all duration-200 hover:from-brand-500 hover:to-brand-400 cursor-pointer min-h-[44px]"
                >
                  Read the product guide
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-xl border border-[#1a2640] bg-white/[0.03] px-6 py-3 text-sm font-semibold text-slate-200 transition-colors hover:border-[#22d3ee]/40 hover:text-[#22d3ee] cursor-pointer min-h-[44px]"
                >
                  Get started
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </PublicPageLayout>
  );
}
